import React, { useCallback, useMemo, useState } from "react";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
    arrayMove,
} from "@dnd-kit/sortable";
import classNames from "classnames";
import { Loader } from "@design-system/Loader/Loader";
import { Counterparty } from "@common/types/counterparty";
import { NormalizedDescriptionInfo } from "@common/types/normalizedDescription";
import {
    RuleGroupType,
    RuleSourceFilter,
    TransactionRuleGroup,
} from "@common/types/transactionRule";
import { KICK_GROUP_ICON } from "@common/constants/rules";
import { useScope } from "@state/scopeContext.lib";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useUpdateRulePosition } from "@api/rules.api";
import { useUpdateRuleGroup } from "@api/ruleGroups.api";
import { isRuleVisibleBySource, resolveRuleSources } from "../utils";
import styles from "../RulesTable.module.scss";
import { SortableGroup } from "./SortableGroup";
import { RulesGroupBody } from "./RulesGroupBody";
import { GroupOverlay } from "./GroupOverlay";
import { RuleOverlay } from "./RuleOverlay";
import { getNewOrderForRuleReorder, useRuleScopeContext } from "./utils";
import { AccountingRulesTable } from "./AccountingRulesTable";

interface RulesTableProps {
    activeTab: RuleGroupType;
    isLoading: boolean;
    groups: TransactionRuleGroup[];
    accountingGroup?: TransactionRuleGroup;
    counterparties: Counterparty[];
    normalizedDescriptions: NormalizedDescriptionInfo[];
    transactionCounts: Record<string, number>;
    matchingCounts?: Record<string, number>;
    ruleSources?: RuleSourceFilter[];
    defaultAccountingTransactionCounts: Record<string, number>;
    routeRuleId: string | null;
    onRouteRuleClose: () => void;
}

export const RulesTable: React.FC<RulesTableProps> = ({
    activeTab,
    isLoading,
    groups,
    counterparties,
    normalizedDescriptions,
    transactionCounts,
    matchingCounts = {},
    ruleSources,
    accountingGroup,
    defaultAccountingTransactionCounts,
    routeRuleId,
    onRouteRuleClose,
}) => {
    const { workspaceId } = useScope();
    const { enableSimilarityRules } = useTypedFlags();
    const [activeId, setActiveId] = useState<string | null>(null);
    const [overId, setOverId] = useState<string | null>(null);
    const [collapsedGroupId, setCollapsedGroupId] = useState<string | null>(
        null,
    );

    const updateRulePosition = useUpdateRulePosition();
    const updateRuleGroup = useUpdateRuleGroup();
    const scopeContext = useRuleScopeContext();

    const sensors = useSensors(useSensor(PointerSensor));

    const isCustomRulesSourceSelected = resolveRuleSources(
        ruleSources,
    ).includes(RuleSourceFilter.CUSTOM);

    const nonKickGroups = useMemo(
        () => groups.filter((g) => g.icon !== KICK_GROUP_ICON),
        [groups],
    );

    const sortableGroups = useMemo(
        () => (isCustomRulesSourceSelected ? nonKickGroups : []),
        [nonKickGroups, isCustomRulesSourceSelected],
    );

    const kickGroups = useMemo(
        () =>
            groups
                .filter((g) => g.icon === KICK_GROUP_ICON)
                .map((g) => ({
                    ...g,
                    rules: g.rules
                        ?.filter((r) =>
                            isRuleVisibleBySource({
                                rule: r,
                                matchingCounts,
                                ruleSources,
                            }),
                        )
                        .toSorted(
                            (a, b) =>
                                Number(!!a.isDisabled) - Number(!!b.isDisabled),
                        ),
                }))
                .filter((g) => (g.rules?.length ?? 0) > 0),
        [groups, matchingCounts, ruleSources],
    );

    const findGroupByRuleId = useCallback(
        (ruleId: string) =>
            groups.find((g) => g.rules?.some((r) => r.id === ruleId)),
        [groups],
    );

    const findRuleById = useCallback(
        (ruleId: string) => {
            for (const g of groups) {
                const rule = g.rules?.find((r) => r.id === ruleId);
                if (rule) {
                    return rule;
                }
            }
            return null;
        },
        [groups],
    );

    const findGroupById = useCallback(
        (groupId: string) => groups.find((g) => g.id === groupId),
        [groups],
    );

    const handleDragStart = (event: any) => {
        setActiveId(event.active.id);

        // Check if we're dragging a group and collapse it
        const isDraggingGroup = groups.some((g) => g.id === event.active.id);
        if (isDraggingGroup) {
            setCollapsedGroupId(event.active.id);
        }
    };

    const handleDragOver = (event: any) => {
        setOverId(event.over?.id ?? null);
    };

    const handleDragEnd = async (event: any) => {
        setActiveId(null);
        setOverId(null);
        setCollapsedGroupId(null); // Clear collapsed group when drag ends
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        // Group reordering
        const activeGroupIndex = groups.findIndex((g) => g.id === active.id);
        // if we have activeGroupIndex, we must find the overGroupIndex, either via groupId, or via containing ruleId
        let overGroupIndex = groups.findIndex((g) => g.id === over.id);

        if (overGroupIndex === -1) {
            overGroupIndex = groups.findIndex((g) =>
                g.rules?.some((r) => r.id === over.id),
            );
        }
        if (activeGroupIndex !== -1 && overGroupIndex !== -1) {
            // Group reordering logic
            const updatedGroups = arrayMove(
                groups,
                activeGroupIndex,
                overGroupIndex,
            );
            const activeGroup = groups[activeGroupIndex];

            // Calculate the new order for the group
            const newOrder =
                ((updatedGroups[overGroupIndex - 1]?.order ?? 0) +
                    (updatedGroups[overGroupIndex + 1]?.order ??
                        updatedGroups.length + 1)) /
                2;

            // Update the group position
            await updateRuleGroup.mutateAsync({
                groupId: activeGroup.id,
                ruleGroup: {
                    name: activeGroup.name,
                    icon: activeGroup.icon,
                    order: newOrder,
                },
            });
            return;
        }

        // Rule reordering or moving between groups
        const sourceGroup = findGroupByRuleId(active.id);
        const targetGroup =
            findGroupByRuleId(over.id) ?? findGroupById(over.id);
        if (!sourceGroup || !targetGroup) {
            return;
        }

        const sourceIndex = sourceGroup.rules?.findIndex(
            (r) => r.id === active.id,
        );
        let targetIndex = targetGroup.rules?.findIndex((r) => r.id === over.id);

        // Handle cases where the target index is not found
        if (targetIndex === -1) {
            // If dropping on the group itself (over.id is a group id)
            if (over.id === targetGroup.id) {
                // Append to the end of the group
                targetIndex = targetGroup.rules?.length ?? 0;
            }
            // If the group is empty or has only one rule
            else if (targetGroup.rules?.length === 0) {
                targetIndex = 0;
            }
            // If dropping on a rule but index not found (shouldn't happen, but just in case)
            else {
                targetIndex = 0;
            }
        }

        if (sourceIndex === undefined || targetIndex === undefined) {
            return;
        }

        const newOrder = getNewOrderForRuleReorder({
            sourceGroup,
            targetGroup,
            sourceRuleIndex: sourceIndex,
            targetRuleIndex: targetIndex,
        });

        const rule = findRuleById(active.id);
        if (!rule) {
            return;
        }

        await updateRulePosition.mutateAsync({
            workspaceId: workspaceId ?? "",
            rule: { ...rule, order: newOrder, groupId: targetGroup.id },
        });
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
        >
            <table className={styles.rulesTable}>
                <thead>
                    <tr>
                        <th
                            className={classNames(
                                styles.th,
                                styles.header,
                                styles.priorityColumn,
                                { [styles.headerBorderCell]: isLoading },
                            )}
                        >
                            Priority
                        </th>
                        <th
                            className={classNames(
                                styles.th,
                                styles.header,
                                styles.groupColumn,
                                { [styles.headerBorderCell]: isLoading },
                            )}
                        >
                            Group
                        </th>
                        {activeTab === RuleGroupType.TRANSFER ? (
                            <>
                                <th
                                    className={classNames(
                                        styles.th,
                                        styles.header,
                                        {
                                            [styles.headerBorderCell]:
                                                isLoading,
                                        },
                                    )}
                                >
                                    From
                                </th>
                                <th
                                    className={classNames(
                                        styles.th,
                                        styles.header,
                                        {
                                            [styles.headerBorderCell]:
                                                isLoading,
                                        },
                                    )}
                                >
                                    To
                                </th>
                                <th
                                    className={classNames(
                                        styles.th,
                                        styles.header,
                                        {
                                            [styles.headerBorderCell]:
                                                isLoading,
                                        },
                                    )}
                                >
                                    Actions
                                </th>
                            </>
                        ) : (
                            <>
                                <th
                                    className={classNames(
                                        styles.th,
                                        styles.header,
                                        {
                                            [styles.headerBorderCell]:
                                                isLoading,
                                        },
                                    )}
                                >
                                    Conditions
                                </th>
                                <th
                                    className={classNames(
                                        styles.th,
                                        styles.header,
                                        {
                                            [styles.headerBorderCell]:
                                                isLoading,
                                        },
                                    )}
                                >
                                    Actions
                                </th>
                                <th
                                    className={classNames(
                                        styles.th,
                                        styles.header,
                                        styles.entityColumn,
                                        {
                                            [styles.headerBorderCell]:
                                                isLoading,
                                        },
                                    )}
                                >
                                    Entity
                                </th>
                            </>
                        )}
                        <th
                            className={classNames(styles.th, styles.header, {
                                [styles.headerBorderCell]: isLoading,
                            })}
                        >
                            Transactions
                        </th>
                    </tr>
                </thead>
                {!isLoading && (
                    <>
                        <SortableContext
                            items={sortableGroups.map((group) => group.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            {sortableGroups.map((group, index) => (
                                <SortableGroup key={group.id} id={group.id}>
                                    {({
                                        listeners: groupListeners,
                                        attributes: groupAttributes,
                                    }: any) => (
                                        <RulesGroupBody
                                            group={group}
                                            overId={overId}
                                            groupListeners={groupListeners}
                                            groupAttributes={groupAttributes}
                                            isGroupDragged={
                                                !!activeId &&
                                                !!findGroupById(activeId)
                                            }
                                            counterparties={counterparties}
                                            normalizedDescriptions={
                                                normalizedDescriptions
                                            }
                                            transactionCounts={
                                                transactionCounts
                                            }
                                            matchingCounts={matchingCounts}
                                            collapsedGroupId={collapsedGroupId}
                                            priority={index + 1}
                                            scopeContext={scopeContext}
                                            routeRuleId={routeRuleId}
                                            onRouteRuleClose={onRouteRuleClose}
                                        />
                                    )}
                                </SortableGroup>
                            ))}
                        </SortableContext>

                        {enableSimilarityRules &&
                            kickGroups.map((group, index) => (
                                <tbody
                                    key={group.id}
                                    className={styles.tableBody}
                                >
                                    <RulesGroupBody
                                        group={group}
                                        overId={null}
                                        groupListeners={{}}
                                        groupAttributes={{}}
                                        isGroupDragged={false}
                                        counterparties={counterparties}
                                        normalizedDescriptions={
                                            normalizedDescriptions
                                        }
                                        transactionCounts={transactionCounts}
                                        matchingCounts={matchingCounts}
                                        priority={
                                            nonKickGroups.length + index + 1
                                        }
                                        scopeContext={scopeContext}
                                        routeRuleId={routeRuleId}
                                        onRouteRuleClose={onRouteRuleClose}
                                    />
                                </tbody>
                            ))}

                        {accountingGroup && (
                            <AccountingRulesTable
                                accountingGroup={accountingGroup}
                                counterparties={counterparties}
                                normalizedDescriptions={normalizedDescriptions}
                                priority={groups.length + 1}
                                defaultAccountingTransactionCounts={
                                    defaultAccountingTransactionCounts
                                }
                            />
                        )}
                    </>
                )}
            </table>

            {isLoading && (
                <div className={styles.loadingContainer}>
                    <Loader />
                </div>
            )}

            <DragOverlay
                dropAnimation={null}
                className={styles.dragOverlayBody}
            >
                {activeId
                    ? (() => {
                          // If dragging a group
                          const overlayGroup = findGroupById(activeId);
                          if (overlayGroup) {
                              return <GroupOverlay group={overlayGroup} />;
                          }
                          // If dragging a rule
                          const overlayRule = findRuleById(activeId);
                          if (overlayRule) {
                              return (
                                  <RuleOverlay
                                      rule={overlayRule}
                                      counterparties={counterparties}
                                      normalizedDescriptions={
                                          normalizedDescriptions
                                      }
                                  />
                              );
                          }
                          return null;
                      })()
                    : null}
            </DragOverlay>
        </DndContext>
    );
};
